import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'

const navItems = [
  { name: "HOME", path: "/" },
  { name: "SERVICES", path: "/services" },
  { name: "PORTFOLIO", path: "/portfolio" },
  { name: "COMPANY", path: "/company" },
  { name: "PRICING", path: "#", hasDropdown: true },
  { name: "CONTACT", path: "/contact" }
]

const pricingOptions = [
  { name: "Vector Art", path: "/pricing/vector" },
  { name: "Digitization", path: "/pricing/digitization" },
  { name: "Patches", path: "/pricing/patches" }
]

const focusRing =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [pricingDropdownOpen, setPricingDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // close menus whenever the page changes
  useEffect(() => {
    setIsOpen(false)
    setPricingDropdownOpen(false)
  }, [location.pathname])

  const isActive = (item) =>
    item.hasDropdown
      ? location.pathname.startsWith('/pricing')
      : location.pathname === item.path

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-200'
          : 'bg-white border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-3 rounded-lg ${focusRing}`}>
            <motion.img
              src="/logo.jpg"
              alt="Vector Vision"
              className="h-11 w-11 rounded-lg"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            />
            <span className="text-xl font-extrabold tracking-tight text-slate-900">
              Vector<span className="text-[#F97316]">Visionary</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) =>
              item.hasDropdown ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setPricingDropdownOpen(true)}
                  onMouseLeave={() => setPricingDropdownOpen(false)}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={pricingDropdownOpen}
                    onClick={() => setPricingDropdownOpen((v) => !v)}
                    className={`relative flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${focusRing} ${
                      isActive(item) || pricingDropdownOpen
                        ? 'text-[#F97316]'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {item.name}
                    <motion.svg
                      className="w-4 h-4"
                      animate={{ rotate: pricingDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                    {isActive(item) && (
                      <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-[#F97316]" />
                    )}
                  </button>

                  <AnimatePresence>
                    {pricingDropdownOpen && (
                      /* pt-2 keeps the hover area continuous between button and menu */
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 top-full w-56 pt-2"
                      >
                        <div
                          role="menu"
                          className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/60"
                        >
                          {pricingOptions.map((option) => (
                            <Link
                              key={option.name}
                              to={option.path}
                              role="menuitem"
                              onClick={() => setPricingDropdownOpen(false)}
                              className={`block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                                location.pathname === option.path
                                  ? 'bg-orange-50 text-[#F97316]'
                                  : 'text-slate-700 hover:bg-orange-50 hover:text-[#F97316]'
                              } ${focusRing}`}
                            >
                              {option.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  aria-current={isActive(item) ? 'page' : undefined}
                  className={`relative rounded-lg px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${focusRing} ${
                    isActive(item) ? 'text-[#F97316]' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {item.name}
                  {isActive(item) && (
                    <span className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-[#F97316]" />
                  )}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              className={`inline-flex items-center rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#F97316] ${focusRing}`}
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
            className={`lg:hidden rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 hover:text-[#F97316] ${focusRing}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-slate-200 bg-white"
          >
            <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
              {navItems.map((item) =>
                item.hasDropdown ? (
                  <div key={item.name} className="py-2">
                    <div className="px-4 pb-2 text-sm font-semibold tracking-wide text-slate-900">{item.name}</div>
                    <div className="ml-4 space-y-1 border-l-2 border-dashed border-orange-200 pl-3">
                      {pricingOptions.map((option) => (
                        <Link
                          key={option.name}
                          to={option.path}
                          onClick={() => setIsOpen(false)}
                          className={`block rounded-lg px-3 py-2 text-sm transition-colors ${focusRing} ${
                            location.pathname === option.path
                              ? 'bg-orange-50 font-semibold text-[#F97316]'
                              : 'text-slate-600 hover:bg-orange-50 hover:text-[#F97316]'
                          }`}
                        >
                          {option.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    aria-current={isActive(item) ? 'page' : undefined}
                    className={`block rounded-lg px-4 py-2.5 text-sm font-semibold tracking-wide transition-colors ${focusRing} ${
                      isActive(item)
                        ? 'bg-orange-50 text-[#F97316]'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
              <div className="pt-3">
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className={`block w-full rounded-xl bg-slate-900 px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-[#F97316] ${focusRing}`}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar